import { EmptyState, Spinner } from '@nexo/ui';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatLongDate } from '../../lib/format';
import { useMe } from '../auth/AuthProvider';
import { ReleaseNotes } from './ReleaseNotes';
import { unseenEntries, useChangelog, useMarkSeen } from './useChangelog';

/** «Novedades»: what changed in every version, newest first. */
export function NewsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const changelog = useChangelog();
  const markSeen = useMarkSeen();
  const log = changelog.data ?? [];
  const latest = log[0]?.version;
  const unseen = new Set(unseenEntries(log, me).map((e) => e.version));

  // Opening the page counts as reading it.
  useEffect(() => {
    if (latest && me.lastSeenVersion !== latest) markSeen.mutate(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per new version
  }, [latest]);

  return (
    <div className="scrollbar-thin h-full overflow-y-auto bg-canvas">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <Link
          to="/app"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted hover:text-text"
        >
          <ArrowLeft className="size-4" /> {t('Volver')}
        </Link>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Sparkles className="size-6 text-highlight" /> {t('Novedades')}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {t('Lo que hemos ido mejorando en Nexo, versión a versión.')}
        </p>
        {changelog.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : log.length === 0 ? (
          <EmptyState title={t('Aún no hay novedades')} />
        ) : (
          <ol className="mt-8 flex flex-col gap-4">
            {log.map((entry) => (
              <li
                key={entry.version}
                className="rounded-2xl border border-border bg-surface p-5 shadow-panel"
              >
                <h2 className="mb-3 flex flex-wrap items-baseline gap-x-2">
                  <span className="text-base font-semibold">
                    {t('Versión {{version}}', { version: entry.version })}
                  </span>
                  <span className="text-xs text-subtle">{formatLongDate(entry.publishedAt)}</span>
                  {unseen.has(entry.version) && (
                    <span className="rounded-full bg-highlight px-2 text-[11px] font-semibold text-highlight-fg">
                      {t('Nuevo')}
                    </span>
                  )}
                </h2>
                <ReleaseNotes notes={entry.notes} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
